<script>
    import { goBack, store, triggerValidation } from '../../Store.svelte';
    import { onMount } from 'svelte';
    import { navigate, link } from 'svelte-routing';
    import { uploadDocuments } from '../../clients/FilesClient.svelte';
    import Modal from '../../components/Modal.svelte';
    import { personalDetailsFields } from '../../Validators.svelte';
    import Preloader from '../../components/Preloader.svelte';

    $: messageInfoError = 'Error interno';
    $: isValidFileError = false;
    $: isValidProofOfAddressError = false;
    $: isLoading = false;

    let DNIFile = null;
    let pdfError = null;
    let proofOfAddressFile = null;
    let proofOfAddressError = null;
    let loadingMessage =
        'Estamos procesando tu solicitud. Esto solo tomará unos momentos.';

    const MAX_FILE_SIZE = 5 * 1024 * 1024;

    function getBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64String = reader.result.split(',')[1];
                resolve(base64String);
            };
            reader.onerror = (error) => reject(error);
        });
    }

    let valContent = async () => {
        isValidProofOfAddressError = proofOfAddressFile == null;
        isValidFileError = DNIFile == null;

        if (isValidFileError) pdfError = 'Agregue un archivo válido.';
        if (isValidProofOfAddressError)
            proofOfAddressError = 'Agregue un archivo PDF válido.';

        if (
            $store.requestedServices.isAutoinstallation &&
            !isValidFileError &&
            !isValidProofOfAddressError
        ) {
            if (
                DNIFile.size > MAX_FILE_SIZE ||
                proofOfAddressFile.size > MAX_FILE_SIZE
            ) {
                pdfError = 'Los archivos no deben ser mayores a 5MB.';
                return;
            }

            const [dniBase64, proofBase64] = await Promise.all([
                getBase64(DNIFile),
                getBase64(proofOfAddressFile),
            ]);

            isLoading = true;
            const response = await Promise.all([
                uploadDocuments(
                    $store.purchaseStatus.accountNumber,
                    $store.purchaseStatus.accountId,
                    DNIFile.name.split('.')[0],
                    DNIFile.type.split('/')[1],
                    dniBase64
                ),
                uploadDocuments(
                    $store.purchaseStatus.accountNumber,
                    $store.purchaseStatus.accountId,
                    proofOfAddressFile.name.split('.')[0],
                    proofOfAddressFile.type.split('/')[1],
                    proofBase64
                ),
            ]);
            if (response && response.some((item) => item.code === '100')) {
                isLoading = false;
                MicroModal.show('modalError');
            } else {
                $store.purchaseStatus.dniName = DNIFile.name;
                $store.purchaseStatus.proofOfAddressFile =
                    proofOfAddressFile.name;
                isLoading = false;
                navigate('/compra/resumen', { replace: false });
            }
        }
    };

    $: {
        if ($goBack) {
            navigate('/compra/resumen', { replace: false });
            goBack.set(false);
        }

        if ($triggerValidation) {
            valContent();
            triggerValidation.set(false);
        }
    }

    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];

    function handleProofOfAddressUpload(event) {
        const input = event.target;
        const file = input?.files?.[0];

        if (file && allowedTypes.includes(file.type)) {
            if (file.size > MAX_FILE_SIZE) {
                proofOfAddressFile = null;
                proofOfAddressError = 'El archivo no debe ser mayor a 5MB.';
                return;
            }

            const date = new Date().toISOString().split('T')[0];
            const fileExtension = file.type.split('/')[1];
            const newFileName = `comprobante_domicilio_${date}.${fileExtension}`;

            proofOfAddressFile = new File([file], newFileName, {
                type: file.type,
            });
            proofOfAddressError = null;
            isValidProofOfAddressError = false;
        } else {
            proofOfAddressFile = null;
            proofOfAddressError = 'Solo se permiten archivos PDF, PNG o JPG.';
        }
    }

    function removeProofOfAddressFile() {
        proofOfAddressFile = null;
    }

    function handleDNIUpload(event) {
        const input = event.target;
        const file = input?.files?.[0];

        if (file && allowedTypes.includes(file.type)) {
            if (file.size > MAX_FILE_SIZE) {
                DNIFile = null;
                pdfError = 'El archivo no debe ser mayor a 5MB.';
                return;
            }

            const date = new Date().toISOString().split('T')[0];
            const fileExtension = file.type.split('/')[1];
            const newFileName = `identificacion_${date}.${fileExtension}`;

            DNIFile = new File([file], newFileName, { type: file.type });
            pdfError = null;
            isValidFileError = false;
        } else {
            DNIFile = null;
            pdfError = 'Solo se permiten archivos PDF, PNG o JPG.';
        }
    }

    function removeFile() {
        DNIFile = null;
    }

    onMount(() => {
        window.document.dispatchEvent(
            new Event('DOMContentLoaded', {
                bubbles: true,
                cancelable: true,
            })
        );
        $store.settings.location = { progress: 60, step: 3 };
    });

    let fields = personalDetailsFields;
</script>

<div>
    <div class="pure-g justify-content-center">
        <div class="pure-u-1 pure-u-lg-2-3">
            <div class="pure-g align-items-center m-b-20">
                <div class="pure-u-1 sm-m-b-20">
                    <h1 class="text-fs-26 m-b-0">Datos del titular</h1>
                </div>
            </div>
            {#if isLoading}
                <div class="small-loader">
                    <Preloader status={loadingMessage} />
                </div>
            {:else}
                <div class="panel m-b-30">
                    <p
                        class="d-flex justify-content-between align-items-center"
                    >
                        Ingresa los siguientes datos para configurar al titular
                        de la cuenta y personalizar tu paquete a tu medida.
                    </p>
                    <div class="pure-g m-b-20">
                        {#if $store.requestedServices.isAutoinstallation}
                            <div class="pure-u-1">
                                <label
                                    for="curp_pdf"
                                    class="file-input"
                                    class:file-input-error={pdfError ||
                                        isValidFileError}
                                >
                                    <div class="label">
                                        Identificación oficial <span
                                            class="required-asterisk">*</span
                                        >
                                    </div>

                                    {#if !DNIFile && !$store.purchaseStatus.dniName}
                                        <div class="link">Subir archivo</div>
                                        <input
                                            type="file"
                                            id="curp_pdf"
                                            name="curp_pdf"
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            on:change={(e) =>
                                                handleDNIUpload(e)}
                                            class="hidden"
                                        />
                                        <img
                                            src="/assets/img/icons/upload-cloud.svg"
                                            alt="Subir archivo"
                                            class="upload-icon"
                                        />
                                    {:else}
                                        <div class="file-info">
                                            <img
                                                src="/assets/img/icons/file.svg"
                                                alt="Archivo"
                                                class="file-icon"
                                            />

                                            <span class="file-name"
                                                >{$store.purchaseStatus.dniName
                                                    ? $store.purchaseStatus
                                                          .dniName
                                                    : DNIFile.name}</span
                                            >

                                            {#if !$store.purchaseStatus.dniName}
                                                <button
                                                    class="remove-file-button-icon"
                                                    on:click={removeFile}
                                                    aria-label="Quitar archivo"
                                                >
                                                    <img
                                                        src="/assets/img/icons/close.svg"
                                                        alt="Quitar archivo"
                                                        class="icon-cancel"
                                                    />
                                                </button>
                                            {/if}
                                        </div>
                                    {/if}
                                </label>
                            </div>
                            {#if pdfError || isValidFileError}
                                <label class="file-error" for="curp_pdf"
                                    >{pdfError}</label
                                >
                            {/if}

                            <div class="pure-u-1 m-t-20">
                                <label
                                    for="proof_of_address"
                                    class="file-input"
                                    class:file-input-error={proofOfAddressError ||
                                        isValidProofOfAddressError}
                                >
                                    <div class="label">
                                        Comprobante de domicilio <span
                                            class="required-asterisk">*</span
                                        >
                                    </div>

                                    {#if !proofOfAddressFile && !$store.purchaseStatus.proofOfAddressFile}
                                        <div class="link">Subir archivo</div>
                                        <input
                                            type="file"
                                            id="proof_of_address"
                                            name="proof_of_address"
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            on:change={(e) =>
                                                handleProofOfAddressUpload(e)}
                                            class="hidden"
                                        />
                                        <img
                                            src="/assets/img/icons/upload-cloud.svg"
                                            alt="Subir archivo"
                                            class="upload-icon"
                                        />
                                    {:else}
                                        <div class="file-info">
                                            <img
                                                src="/assets/img/icons/file.svg"
                                                alt="Archivo"
                                                class="file-icon"
                                            />

                                            <span class="file-name"
                                                >{$store.purchaseStatus
                                                    .proofOfAddressFile
                                                    ? $store.purchaseStatus
                                                          .proofOfAddressFile
                                                    : proofOfAddressFile.name}</span
                                            >
                                            {#if !$store.purchaseStatus.proofOfAddressFile}
                                                <button
                                                    class="remove-file-button-icon"
                                                    on:click={removeProofOfAddressFile}
                                                    aria-label="Quitar archivo"
                                                >
                                                    <img
                                                        src="/assets/img/icons/close.svg"
                                                        alt="Quitar archivo"
                                                        class="icon-cancel"
                                                    />
                                                </button>
                                            {/if}
                                        </div>
                                    {/if}
                                </label>
                            </div>

                            {#if proofOfAddressError || isValidProofOfAddressError}
                                <label
                                    class="file-error"
                                    for="proof_of_address"
                                >
                                    {proofOfAddressError}
                                </label>
                            {/if}
                        {/if}
                    </div>

                    <div
                        class="d-flex align-items-center justify-content-between"
                    >
                        <a use:link href="/compra/direccion" class="back-link">
                            <svg class="icon" aria-hidden="true"
                                ><use
                                    xlink:href="/assets/img/icons/symbol-defs.svg#icon-arrow-left"
                                /></svg
                            >
                            <span>atrás</span>
                        </a>
                        <div>
                            <a
                                href="#/"
                                class="pure__button pure__button--base button-hide"
                                on:click|preventDefault={() => valContent()}
                                >Continuar</a
                            >
                        </div>
                    </div>
                </div>
            {/if}
        </div>
    </div>
</div>

<Modal ident="modalError" title="Lo sentimos" message={messageInfoError} />
